import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import React, { useContext, useEffect, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import moment from 'moment'
import AnDateField from '../../../../components/DateTime/DateField'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../services/API'
import { DetailRow } from '../../../../components/Cards/DetailsBox'
import { GlobalContext, TenantSettingsContext } from '../../../../App'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import RecordPayment from '../../../admin/finance/Invoices/RecordPayment'
import { EventMetaContext } from '../SingleEvent'
import { vatMultiplierForItem, vatOptions } from '../../../../helpers/region'

const InvoiceGenerator = ({
  open,
  onClose,
  createInvoiceFromQuote,
  existingInvoice,
  loadInvoices,
}) => {
  const { setToast } = useContext(GlobalContext)
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { orgSettings } = { ...orgConfig }
  const region = tenantInfo?.region
  const { eventMeta } = useContext(EventMetaContext)
  const [invoiceReference, setInvoiceReference] = useState('')
  const defaultValues = [
    { id: 0, description: '', quantity: '', unitPrice: '', vat: 'none' },
  ]
  const [items, setItems] = useState(defaultValues)
  const [invoiceDate, setInvoiceDate] = useState(moment().startOf('day'))
  const [dueDate, setDueDate] = useState(moment().startOf('day').add(7, 'days'))
  const [discount, setDiscount] = useState(0)
  const [sendToClient, setSendToClient] = useState(true)

  const rowsWithValues = items?.filter(
    (x) => x.description !== '' && x.quantity !== '' && x.unitPrice !== '',
  )

  useEffect(() => {
    if (open && !existingInvoice) {
      API.get('/finance/next-invoice-number').then((res) => {
        if (res.status === 200) {
          setInvoiceReference(res.data)
        }
      })
    } else if (!existingInvoice) {
      setInvoiceReference('')
      setItems(defaultValues)
      setInvoiceDate(moment().startOf('day'))
      setDueDate(moment().startOf('day').add(7, 'days'))
      setSendToClient(true)
      setDiscount(0)
    }
  }, [open])

  const handleItemCellChange = (id, field, value) => {
    const updatedItems = items.map((item) =>
      item.id === id ? { ...item, [field]: value } : item,
    )
    setItems(updatedItems)
    ensureEmptyLine(updatedItems)
  }
  const [defaultSettings, setDefaultSettings] = useState(null)

  useEffect(() => {
    API.get('/settings?select=quoteInvoices').then((res) => {
      if (res.status === 200) {
        setDefaultSettings(res.data.quoteInvoices)
      }
    })
  }, [])

  const ensureEmptyLine = (items) => {
    const lastItem = items[items.length - 1]
    if (lastItem.description || lastItem.quantity || lastItem.unitPrice) {
      setItems([
        ...items,
        {
          id: items.length + 1,
          description: '',
          quantity: '',
          unitPrice: '',
        },
      ])
    }
  }

  useEffect(() => {
    if (createInvoiceFromQuote) {
      API.get('/clients/quote?quoteID=' + createInvoiceFromQuote._id).then(
        (res) => {
          if (res.status !== 200 || !res.data?.summary) {
            setToast({
              message:
                res?.data?.error || 'Unable to load the quote for this invoice.',
              severity: 'error',
            })
            return
          }
          let dataToAdd = []
          dataToAdd.push({
            description: 'Personnel',
            unitPrice: res.data.summary.personnel,
            quantity: 1,
          })
          dataToAdd.push({
            description: 'Administration, Logistics & Build',
            unitPrice:
              (res.data.summary.administration ?? 0) +
              (res.data.treatmentCentreBuilds ?? 0),
            quantity: 1,
          })
          if (
            res.data.chargeAccommodationToCustomer &&
            res.data.accommodationCharge > 0
          ) {
            dataToAdd.push({
              description: 'Accommodation',
              quantity: 1,
              unitPrice: res.data.accommodationCharge,
            })
          }

          // summary.vehiclesMileage always carries the vehicle charge
          // (make-ready cost + margin), with mileage folded in only when it
          // is charged - gating this line on chargeMileageToCustomer used to
          // silently drop the whole vehicle charge off the invoice
          if (res.data.summary?.vehiclesMileage > 0) {
            dataToAdd.push({
              description: `Vehicles${
                res.data?.chargeMileageToCustomer ? ' & Mileage' : ''
              }`,
              quantity: 1,
              unitPrice: res.data.summary.vehiclesMileage,
            })
          }
          // Re-index sequentially by position rather than trusting
          // hardcoded ids — skipped conditional items (e.g. no
          // accommodation) would otherwise leave gaps that collide with
          // the id ensureEmptyLine computes from items.length.
          dataToAdd = dataToAdd.map((item, index) => ({ ...item, id: index + 1 }))
          setItems(dataToAdd)
          ensureEmptyLine(dataToAdd)
          setDiscount(res.data.discount || 0)
        },
      )
    } else if (existingInvoice?._id) {
      // Re-hydrate on every open, not just when the invoice reference
      // changes - Cancel used to leave abandoned edits behind for the next
      // open to show as if they were saved
      setInvoiceReference(existingInvoice.reference)
      setItems([...(existingInvoice.items || []), ...defaultValues])
      setInvoiceDate(existingInvoice.invoiceDate)
      setDueDate(existingInvoice.dueDate)
      setDiscount(existingInvoice.discount || 0)
    }
  }, [createInvoiceFromQuote, existingInvoice, open])

  const _invoiceSubtotal = items?.reduce((acc, item) => {
    const quantity = item.quantity === '' ? 0 : parseFloat(item.quantity)
    const unitPrice = item.unitPrice === '' ? 0 : parseFloat(item.unitPrice)
    const amount = quantity * unitPrice
    acc += amount
    return acc
  }, 0)

  const _invoiceVATtotal = items?.reduce((acc, item) => {
    const quantity = item.quantity === '' ? 0 : parseFloat(item.quantity)
    const unitPrice = item.unitPrice === '' ? 0 : parseFloat(item.unitPrice)
    const rate = vatMultiplierForItem(item.vat, region) - 1
    return acc + quantity * unitPrice * rate
  }, 0)

  // status is only sent for a deliberate transition (new-as-Draft,
  // new-as-Approved, or Draft→Approved). A plain edit omits it so the
  // server keeps the stored status - "Save & Close" used to silently demote
  // Approved invoices back to Draft.
  const handleSaveClose = ({ status } = {}) => {
    const data = {
      invoiceID: existingInvoice?._id || false,
      organisation: eventMeta?.organisation?._id,
      event: eventMeta?._id,
      invoiceDate,
      dueDate,
      items: rowsWithValues,
      ...(status ? { status } : {}),
      discount,
    }
    API.post('/finance/invoice', data)
      .then((res) => {
        if (res.status === 200) {
          onClose()
          loadInvoices && loadInvoices()
          setToast({
            message: existingInvoice
              ? status === 'Approved'
                ? 'Invoice Approved'
                : 'Invoice Saved'
              : 'Invoice Created',
          })
        } else {
          setToast({
            message: res?.data?.error || 'Unable to save invoice.',
            severity: 'error',
          })
        }
      })
      .catch(() => {
        setToast({
          message: 'Unable to save invoice. Please try again later.',
          severity: 'error',
        })
      })
  }

  return (
    <BigModal open={open} onClose={onClose} bgcolor="#fff">
      <Grid spacing={2} padding={1} container>
        <Grid item xs={12}>
          <Typography variant="title2" textAlign="center">
            {existingInvoice
              ? `Edit Invoice ${existingInvoice.reference ?? ''}`
              : `Create Invoice${eventMeta?.title ? ` for ${eventMeta.title}` : ''}`}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <TextField
            label="Reference"
            value={invoiceReference}
            onChange={(e) => setInvoiceReference(e.target.value)}
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <AnDateField
            date={invoiceDate}
            onChange={(newDate) => setInvoiceDate(newDate)}
            label="Invoice Date"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <AnDateField
            date={dueDate}
            onChange={(newDate) => setDueDate(newDate)}
            label="Due Date"
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Table
            sx={{
              border: 'solid 1px #e0e0e0',
              backgroundColor: '#fff',
              borderRadius: 2,
            }}
            color="primary"
            variant="soft"
          >
            <TableHead>
              <TableRow>
                <TableCell
                  width="52%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Description
                </TableCell>
                <TableCell
                  width="12%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  QTY
                </TableCell>
                <TableCell
                  width="12%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  Price
                </TableCell>
                <TableCell
                  width="12%"
                  sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}
                >
                  VAT
                </TableCell>
                <TableCell width="12%" sx={{ fontWeight: 'bold' }}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items?.map((item) => (
                <TableRow key={item.id}>
                  <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                    <InputBase
                      fullWidth
                      value={item.description}
                      onChange={(e) =>
                        handleItemCellChange(
                          item.id,
                          'description',
                          e.target.value,
                        )
                      }
                      autoComplete="off"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ borderRight: '1px solid #e0e0e0' }}
                    align="center"
                  >
                    <InputBase
                      fullWidth
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemCellChange(
                          item.id,
                          'quantity',
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: '1px solid #e0e0e0',
                    }}
                  >
                    <InputBase
                      fullWidth
                      type="number"
                      value={item.unitPrice}
                      onChange={(e) => {
                        handleItemCellChange(
                          item.id,
                          'unitPrice',
                          e.target.value,
                        )
                      }}
                      startAdornment={
                        item.unitPrice && (
                          <InputAdornment position="start">
                            {orgSettings?.currency ?? '£'}
                          </InputAdornment>
                        )
                      }
                    />
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                    <Select
                      value={item.vat ?? 'none'}
                      fullWidth
                      size="small"
                      onChange={(e) =>
                        handleItemCellChange(item.id, 'vat', e.target.value)
                      }
                    >
                      {vatOptions(region).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    {(item.quantity ?? 0) * (item.unitPrice ?? 0) > 0 &&
                      `${orgSettings?.currency ?? '£'}${parseFloat(
                        (item.quantity ?? 0) * (item.unitPrice ?? 0),
                      ).toFixed(2)}`}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
        {/* The Payment Schedule editor that used to sit here was dead UI -
            its values were never sent to the server. Removed rather than
            left to mislead. */}
        <Grid item xs={12} lg={6}>
          <Stack alignItems="flex-end" gap={1}>
            <Stack direction="row">
              <Box width="50%">
                <Typography variant="title3">Subtotal</Typography>
              </Box>
              <Box width="50%" minWidth="10rem" textAlign="right">
                {orgSettings?.currency ?? '£'}
                {parseFloat(_invoiceSubtotal).toFixed(2)}
              </Box>
            </Stack>
            <Stack direction="row">
              <Box width="50%" fontWeight="bold" color="primary.main">
                <Typography variant="title3">VAT</Typography>
              </Box>
              <Box width="50%" minWidth="10rem" textAlign="right">
                {orgSettings?.currency ?? '£'}
                {parseFloat(_invoiceVATtotal).toFixed(2)}
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center">
              <Box width="50%">
                <Typography variant="title3">Discount</Typography>
              </Box>
              <Box width="50%" minWidth="10rem" textAlign="right">
                <TextField
                  type="number"
                  size="small"
                  value={discount}
                  onChange={(e) =>
                    setDiscount(
                      e.target.value >= 0 ? parseFloat(e.target.value) : 0,
                    )
                  }
                  InputProps={{
                    inputProps: { min: 0 },
                    startAdornment: (
                      <InputAdornment position="start">
                        {orgSettings?.currency ?? '£'}
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Stack>
            <Stack direction="row">
              <Box width="50%">
                <Typography variant="title3">Total</Typography>
              </Box>
              <Box width="50%" minWidth="10rem" textAlign="right">
                {orgSettings?.currency ?? '£'}
                {parseFloat(
                  (_invoiceSubtotal || 0) +
                    (_invoiceVATtotal || 0) -
                    (discount || 0),
                ).toFixed(2)}
              </Box>
            </Stack>
            <Stack direction="row" gap={1}>
              <Button
                onClick={() => onClose()}
                variant="outlined"
                color="anRed"
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="anBlue"
                onClick={() =>
                  // New invoices are created as Draft; edits keep the
                  // invoice's current status
                  handleSaveClose(existingInvoice ? {} : { status: 'Draft' })
                }
              >
                Save & Close
              </Button>
              {(!existingInvoice || existingInvoice.status === 'Draft') && (
                <Button
                  variant="contained"
                  color="anBlue"
                  onClick={() => handleSaveClose({ status: 'Approved' })}
                >
                  {existingInvoice ? 'Save & Approve' : 'Approve'}
                  {/* TODO: Approve & Email */}
                </Button>
              )}
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </BigModal>
  )
}

export default InvoiceGenerator
